import { useState, useRef } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Upload, X, FileText } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { useCreateEpreuve } from '@/lib/admin/epreuve-mutations';

const EXAM_TYPES = [
  { value: '', label: 'Non spécifié' },
  { value: 'brevet', label: 'Brevet' },
  { value: 'bac', label: 'Bac' },
  { value: 'bac_pro', label: 'Bac Pro' },
  { value: 'controle_commun', label: 'Contrôle commun' },
  { value: 'ds', label: 'DS' },
  { value: 'autre', label: 'Autre' },
] as const;

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2 w-full';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';

export function TutorAdminEpreuvesNewPage() {
  const navigate = useNavigate();
  const createEpreuve = useCreateEpreuve();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState<SubjectSlug | ''>('');
  const [level, setLevel] = useState<SchoolLevel>('3e');
  const [examType, setExamType] = useState('');
  const [examYear, setExamYear] = useState<number | ''>('');

  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [correctionFile, setCorrectionFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const correctionInputRef = useRef<HTMLInputElement>(null);

  function pickFile(e: React.ChangeEvent<HTMLInputElement>, setter: (f: File | null) => void) {
    const file = e.target.files?.[0] ?? null;
    setUploadError(null);
    if (!file) {
      setter(null);
      return;
    }
    if (file.type !== 'application/pdf') {
      setUploadError('Seuls les fichiers PDF sont acceptés.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadError('Le fichier dépasse la limite de 50 Mo.');
      return;
    }
    setter(file);
  }

  /** Upload un PDF dans le bucket `epreuves-pdf` ; renvoie le chemin stocké. */
  async function uploadPdf(file: File, prefix: string): Promise<string> {
    const fileName = `${prefix}${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const { error } = await supabase.storage
      .from('epreuves-pdf')
      .upload(fileName, file, { contentType: 'application/pdf' });
    if (error) throw new Error(error.message);
    return fileName;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;

    setUploading(true);
    setUploadError(null);

    let sourceFileUrl: string | null = null;
    let correctionFileUrl: string | null = null;

    try {
      if (pdfFile) sourceFileUrl = await uploadPdf(pdfFile, '');
      if (correctionFile) correctionFileUrl = await uploadPdf(correctionFile, 'corr_');
    } catch (err) {
      setUploadError(`Erreur upload PDF : ${err instanceof Error ? err.message : String(err)}`);
      setUploading(false);
      return;
    }

    const result = await createEpreuve.mutateAsync({
      title: title.trim(),
      description: description.trim() || null,
      subject: subject || null,
      level,
      exam_type: examType || null,
      exam_year: examYear !== '' ? Number(examYear) : null,
      source_file_url: sourceFileUrl,
      correction_file_url: correctionFileUrl,
    });

    setUploading(false);
    navigate({ to: '/tutor/admin/epreuves/$epreuveId', params: { epreuveId: result!.id } });
  }

  const isLoading = uploading || createEpreuve.isPending;

  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/epreuves"
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Épreuves
      </Link>

      <h1 className="font-display text-ink-900 mb-6 text-[22px] font-extrabold">
        Nouvelle épreuve
      </h1>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Titre */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>Titre</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="ex. Brevet 2023 — Mathématiques"
            className={inputCls}
            required
          />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Description <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full resize-none rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          />
        </div>

        {/* Niveau + Matière */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>Niveau</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as SchoolLevel)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              {SCHOOL_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>
              Matière <span className="text-ink-400 font-normal">(optionnel)</span>
            </label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value as SubjectSlug | '')}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              <option value="">Toutes</option>
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Type + Année */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>Type d'épreuve</label>
            <select
              value={examType}
              onChange={(e) => setExamType(e.target.value)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 w-full rounded-xl border bg-white px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            >
              {EXAM_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>
              Année <span className="text-ink-400 font-normal">(optionnel)</span>
            </label>
            <input
              type="number"
              min={1990}
              max={2100}
              placeholder="2024"
              value={examYear}
              onChange={(e) => setExamYear(e.target.value ? parseInt(e.target.value, 10) : '')}
              className={inputCls}
            />
          </div>
        </div>

        {/* Upload PDF */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            PDF source <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          {pdfFile ? (
            <div className="border-ink-200 flex items-center gap-3 rounded-xl border px-3 py-2.5">
              <FileText className="text-level-500 h-5 w-5 shrink-0" />
              <span className="flex-1 truncate font-sans text-[13px]">{pdfFile.name}</span>
              <button
                type="button"
                onClick={() => {
                  setPdfFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-ink-400 hover:text-ink-700 shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="border-ink-200 hover:border-primary-400 flex items-center gap-3 rounded-xl border-2 border-dashed px-4 py-4 transition-colors"
            >
              <Upload className="text-ink-400 h-5 w-5 shrink-0" />
              <span className="text-ink-500 font-sans text-[13px]">
                Cliquer pour choisir le PDF de l'énoncé (max 50 Mo)
              </span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => pickFile(e, setPdfFile)}
          />
        </div>

        {/* Upload PDF corrigé */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            PDF corrigé <span className="text-ink-400 font-normal">(optionnel)</span>
          </label>
          {correctionFile ? (
            <div className="border-ink-200 flex items-center gap-3 rounded-xl border px-3 py-2.5">
              <FileText className="text-primary-500 h-5 w-5 shrink-0" />
              <span className="flex-1 truncate font-sans text-[13px]">{correctionFile.name}</span>
              <button
                type="button"
                onClick={() => {
                  setCorrectionFile(null);
                  if (correctionInputRef.current) correctionInputRef.current.value = '';
                }}
                className="text-ink-400 hover:text-ink-700 shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => correctionInputRef.current?.click()}
              className="border-ink-200 hover:border-primary-400 flex items-center gap-3 rounded-xl border-2 border-dashed px-4 py-4 transition-colors"
            >
              <Upload className="text-ink-400 h-5 w-5 shrink-0" />
              <span className="text-ink-500 font-sans text-[13px]">
                Cliquer pour choisir le PDF du corrigé (max 50 Mo)
              </span>
            </button>
          )}
          <input
            ref={correctionInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => pickFile(e, setCorrectionFile)}
          />
          {uploadError && <p className="text-accent-600 font-sans text-[12px]">{uploadError}</p>}
          {pdfFile && (
            <p className="text-ink-400 font-sans text-[11px]">
              Après création, lance le pipeline depuis la page de l'épreuve : l'énoncé sera
              décomposé en exercices V2, le corrigé associé aux questions, et les distracteurs
              générés.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Link to="/tutor/admin/epreuves" className="flex-1">
            <Button type="button" variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
          <Button type="submit" className="flex-1" disabled={isLoading || !title.trim()}>
            {isLoading ? 'Création...' : 'Créer'}
          </Button>
        </div>

        {createEpreuve.isError && (
          <p className="text-accent-600 font-sans text-[13px]">{createEpreuve.error?.message}</p>
        )}
      </form>
    </div>
  );
}
